import React, { useState, useEffect, useCallback } from 'react';
import { DiscordWidgetState } from './types';

export const DISCORD_GUILD_ID = '1526789048335863988';
export const DISCORD_INVITE_URL = 'https://discord.gg/m8JjEenjN';

export const DiscordWidget: React.FC = () => {
  const [widgetState, setWidgetState] = useState<DiscordWidgetState>({
    status: 'fetching',
    name: 'The Current of the Unyoked',
    instantInvite: DISCORD_INVITE_URL,
    presenceCount: 0,
    members: []
  });
  const [showIframe, setShowIframe] = useState<boolean>(false);

  const fetchGuildStatus = useCallback(async () => {
    setWidgetState(prev => ({ ...prev, status: 'fetching' }));
    try {
      const response = await fetch(`https://discord.com/api/guilds/${DISCORD_GUILD_ID}/widget.json`, {
        headers: { 'Accept': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setWidgetState({
          status: 'live',
          name: data.name || 'The Current of the Unyoked',
          instantInvite: data.instant_invite || DISCORD_INVITE_URL,
          presenceCount: data.presence_count || 0,
          members: data.members || []
        });
      } else {
        // HTTP 403 or other status -> Fallback to shielded mode
        setWidgetState({
          status: 'shielded',
          name: 'The Current of the Unyoked',
          instantInvite: DISCORD_INVITE_URL,
          presenceCount: 0,
          members: []
        });
      }
    } catch {
      // Network error or CORS restriction -> Fallback to shielded mode
      setWidgetState({
        status: 'shielded',
        name: 'The Current of the Unyoked',
        instantInvite: DISCORD_INVITE_URL,
        presenceCount: 0,
        members: []
      });
    }
  }, []);

  useEffect(() => {
    fetchGuildStatus();
  }, [fetchGuildStatus]);

  return (
    <div className="chthonic-card rounded-lg p-6 sm:p-8 space-y-6" id="discord-widget-container">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-iron-dark pb-4">
        <div>
          <span className="text-[10px] font-mono text-gold-ancient uppercase tracking-widest block">
            PORTA DISCORDIA • GUILD GATEWAY
          </span>
          <h3 className="font-display text-xl sm:text-2xl text-parchment-light mt-1">
            The Subterranean Conclave
          </h3>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono uppercase tracking-wider border ${
            widgetState.status === 'live'
              ? 'border-emerald-500/70 bg-emerald-950/40 text-emerald-400'
              : widgetState.status === 'fetching'
              ? 'border-gold-bright/60 bg-gold-ancient/10 text-gold-bright animate-pulse'
              : 'border-iron-base bg-obsidian-deep text-parchment-dim'
          }`}>
            <span className={`h-2 w-2 rounded-full ${
              widgetState.status === 'live' ? 'bg-emerald-400' :
              widgetState.status === 'fetching' ? 'bg-gold-bright' : 'bg-iron-steel'
            }`} />
            {widgetState.status === 'live' ? 'GATEWAY LIVE' :
             widgetState.status === 'fetching' ? 'SCANNING...' : 'SANCTUM SHIELDED'}
          </span>
        </div>
      </div>

      {/* Guild Metadata Card */}
      <div className="rounded-lg border border-iron-dark/80 bg-obsidian-deep/90 p-5 sm:p-6 space-y-4 font-serif text-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-iron-dark/50 pb-3">
          <span className="font-display tracking-widest text-iron-steel text-[11px] uppercase">
            COMMUNITY:
          </span>
          <span className="font-display text-sm font-semibold text-gold-bright">
            {widgetState.name}
          </span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-iron-dark/50 pb-3">
          <span className="font-display tracking-widest text-iron-steel text-[11px] uppercase">
            CONCLAVE SERVER ID:
          </span>
          <span className="font-mono text-gold-ancient tracking-wider text-xs">
            {DISCORD_GUILD_ID}
          </span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-iron-dark/50 pb-3">
          <span className="font-display tracking-widest text-iron-steel text-[11px] uppercase">
            CANONICAL PORTAL DOMAIN:
          </span>
          <a
            href="https://tlb.lilitu.org"
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-gold-bright tracking-wider text-xs hover:underline hover:text-gold-radiant transition-colors"
          >
            tlb.lilitu.org
          </a>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-iron-dark/50 pb-3">
          <span className="font-display tracking-widest text-iron-steel text-[11px] uppercase">
            SANCTUM STATUS:
          </span>
          <span className="text-parchment-light font-mono text-[11px]">
            {widgetState.status === 'live' 
              ? `${widgetState.presenceCount} INITIATES ONLINE IN SANCTUARY`
              : 'CONSECRATED IN BEDROCK • ANONYMOUS ENTRY'}
          </span>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <span className="font-display tracking-widest text-iron-steel text-[11px] uppercase">
            PRIMARY COVENANT:
          </span>
          <span className="text-ember-flare font-serif italic text-xs">
            Sine Iugo, Sine Domino
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <a
          href={widgetState.instantInvite || DISCORD_INVITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-gold flex-1 text-center py-3 text-xs font-display tracking-widest uppercase rounded shadow-glow-bronze flex items-center justify-center gap-2"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994.021-.041.001-.09-.041-.106a13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.929 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.894.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028z" />
          </svg>
          Enter Sovereign Conclave (Join Discord)
        </a>

        <button
          onClick={fetchGuildStatus}
          aria-label="Re-Scan Gateway Status"
          className="rounded border border-iron-base bg-obsidian-surface px-4 py-3 text-xs font-mono text-parchment-dim hover:border-gold-bright hover:text-gold-bright transition-colors"
        >
          Re-Scan Gateway
        </button>

        <button
          onClick={() => setShowIframe(!showIframe)}
          className="rounded border border-iron-base bg-obsidian-surface px-4 py-3 text-xs font-mono text-parchment-dim hover:text-parchment-light transition-colors"
        >
          {showIframe ? 'Hide Embed' : 'View Iframe Embed'}
        </button>
      </div>

      {/* Optional Iframe Embed Fallback */}
      {showIframe && (
        <div className="rounded-lg border border-iron-dark overflow-hidden bg-black p-2 animate-fade-in">
          <iframe
            src={`https://discord.com/widget?id=${DISCORD_GUILD_ID}&theme=dark`}
            width="100%"
            height="360"
            title="Discord Server Widget"
            sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
            className="border-0 w-full"
          />
        </div>
      )}
    </div>
  );
};

export default DiscordWidget;
